import {
	OrderAdjustmentCalculationType,
	OrderAdjustmentType,
	OrderSampleChoice,
} from '@codezo/graphql';
import {
	AdjustmentsSection,
	AttachmentsSection,
	SamplesSection,
	type ExtrasProduct,
	type SectionFrame,
} from '@codezo/offer-extras';
import { CurrencyText } from '../../../../components/CurrencyText';
import { FilePreview } from '../../../../components/ui/file-preview';
import { treatmentMarker } from '../../../../lib/sample-treatment';
import type { ClientQuoteItem, Offer } from './offer-types';

/**
 * The client card's samples / adjustments / attachments sections.
 *
 * The layout lives in `@codezo/offer-extras`, shared with the admin card so the
 * two can't drift. This file is the adapter: it flattens the client's offer
 * shape into the shared contract and supplies the app's own primitives.
 */

export function toExtrasProducts(
	offers: Offer[],
	itemLookup: Map<string, ClientQuoteItem>,
	/** Products taken out of the order. They keep their rows, dimmed and locked,
	 * rather than disappearing — the product card above already says
	 * "Not included" and leaves the product on screen, so a row that vanishes
	 * here answered the same question a second, different way. */
	excludedOfferIds: Set<string>
): ExtrasProduct[] {
	return offers.map((offer) => ({
		offerId: offer.id,
		itemId: offer.orderQuoteItemId,
		name: itemLookup.get(offer.orderQuoteItemId)?.productName || offer.name,
		excluded: excludedOfferIds.has(offer.id),
		sample: offer.sample
			? {
					id: offer.sample.id,
					totalPrice: offer.sample.totalPrice ?? null,
					minProductionDays: offer.sample.minProductionDays ?? null,
					maxProductionDays: offer.sample.maxProductionDays ?? null,
					deliveryDaysMin: offer.sample.deliveryDaysMin ?? null,
					deliveryDaysMax: offer.sample.deliveryDaysMax ?? null,
					notes: offer.sample.notes,
					// Worded for the person paying — "your order". Admin's copy of this
					// says "from order"; both match their side of the quote PDF.
					treatmentNote: treatmentMarker(offer.sample.paymentTreatment),
					pricesByChoice: offer.sample.pricesByChoice,
					// No breakdown: splitting the price into sample and freight is an
					// authoring detail, and the client is quoted one figure per choice.
				}
			: null,
		adjustments: (offer.adjustments ?? []).map((adj) => ({
			id: adj.id,
			name: adj.name,
			description: adj.description,
			isFee: adj.type === OrderAdjustmentType.Fee,
			isPercentage: adj.calculationType === OrderAdjustmentCalculationType.Percentage,
			value: adj.value,
		})),
		attachments: (offer.attachments ?? []).map((f) => ({
			id: f.id,
			name: f.name,
			description: f.description,
			mimeType: f.mimeType,
			size: f.size,
			url: f.url,
		})),
	}));
}

export function ClientSamplesSection({
	products,
	sampleChoices,
	onSelectSampleChoice,
}: {
	products: ExtrasProduct[];
	sampleChoices: Record<string, OrderSampleChoice>;
	/** Omitted on an accepted batch, where the combination is a record rather
	 * than a choice — the tiles then render as a priced, read-only preview. */
	onSelectSampleChoice?: (offerId: string, choice: OrderSampleChoice) => void;
}) {
	return (
		<SamplesSection
			products={products}
			CurrencyText={CurrencyText}
			choices={{
				selected: sampleChoices,
				// The shared section speaks in raw enum values so it needn't depend on
				// the schema; they are the same strings this enum is built from.
				onSelect: onSelectSampleChoice
					? (offerId, choice) =>
							onSelectSampleChoice(offerId, choice as OrderSampleChoice)
					: undefined,
			}}
		/>
	);
}

export function ClientAdjustmentsSection({
	products,
	frame,
}: {
	products: ExtrasProduct[];
	frame?: SectionFrame;
}) {
	return <AdjustmentsSection products={products} CurrencyText={CurrencyText} frame={frame} />;
}

/** v1 only: the v2 card lists a product's files under the product, in the
 * requested list, where they belong. See the note on its fees section. */
export function ClientAttachmentsSection({ products }: { products: ExtrasProduct[] }) {
	return <AttachmentsSection products={products} FilePreview={FilePreview} />;
}
